import React, {useState, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import {
  Button,
  IconButton,
  Modal,
  Dialogue,
  TextField,
  SelectField,
  Form,
  Icon,
  LoadingSpinner,
  Text,
  Anchor,
  addToast
} from '@albert-io/atomic';
import classNames from 'classnames';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {getSchoolOptionsWithMasqueradeState} from 'generic/Masquerade/helpers';
import {getTeacherQuery} from 'client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react';

import useAsyncFunctionStatus from 'lib/hooks/useAsyncFunctionStatus';
import {useGetGradeLevelsQuery} from 'gql/shared/GradeLevels.queries.generated';

import GradeSelector from '../../shared/GradeSelector/GradeSelector.react';

import LicenseStatusRadioGroup from '../ClassroomsList/LicenseStatus/LicenseStatusRadioGroup';

import {
  validateForm,
  createClassroom,
  invalidateClassroomsQueries,
  formValidators
} from './newClassroomModal.utils';

export const AddClassButton = withRouter(function AddClassButton(props) {
  'use memo';

  const buttonText = props.label || 'New class';
  const isDefaultOpen = Object.prototype.hasOwnProperty.call(
    props.location.query,
    'createClassroomModal'
  );
  const [isOpen, setIsOpen] = useState(props.handleDefaultOpen && isDefaultOpen);
  const toggleIsOpen = () => setIsOpen((currentIsOpen) => !currentIsOpen);
  return (
    <>
      <Button onClick={toggleIsOpen} variant={props.variant}>
        <Icon icon='plus' className='mr-x1' />
        {buttonText}
      </Button>
      {isOpen && <NewClassroomModal handleClose={toggleIsOpen} />}
    </>
  );
});

export function NewClassroomModal({handleClose, onSubmit}) {
  'use memo';

  const [formErrors, setFormErrors] = useState({});
  const [selectedGradeLevels, setSelectedGradeLevels] = useState([]);
  const [applyStudentsToSchoolLicense, setApplyStudentsToSchoolLicense] = useState(true);
  const [teacher, setTeacher] = useState(null);
  const [isTeacherAndSchoolsReady, setIsTeacherAndSchoolsReady] = useState(false);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  const {data: gradeLevelsData, error: gradeLevelsError} = useGetGradeLevelsQuery();

  const {status: createClassroomStatus, callback: createClassroomCb} =
    useAsyncFunctionStatus(createClassroom);

  const isCreateClassroomPending = createClassroomStatus === 'pending';

  // Server-sorted via `PADDED_GRADE_LEVEL_TYPE` — no client-side sort needed.
  const gradeLevelResults = gradeLevelsData?.gradeLevels?.results;
  const allGradeLevels = useMemo(
    () =>
      (gradeLevelResults ?? []).map((gradeLevel) => ({
        getId: () => gradeLevel.id,
        getGradeLevelType: () => gradeLevel.gradeLevelType ?? ''
      })),
    [gradeLevelResults]
  );

  const isReady = isTeacherAndSchoolsReady && gradeLevelResults !== undefined;

  useEffect(() => {
    let mounted = true;
    Promise.all([
      getSchoolOptionsWithMasqueradeState(masqueradeStore.getUserIdByMasqueradeState()),
      getTeacherQuery().getResourcePromise()
    ])
      .then(([returnedSchoolOptions, returnedTeacher]) => {
        if (mounted) {
          setSchoolOptions(returnedSchoolOptions);
          setTeacher(returnedTeacher);
          setIsTeacherAndSchoolsReady(true);
        }
      })
      .catch(() => {
        addToast({
          color: 'negative',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (gradeLevelsError) {
      addToast({
        color: 'negative',
        title: 'Error',
        message: 'Something went wrong with your request. Please try again.'
      });
    }
  }, [gradeLevelsError]);

  const getIsLicensedForSchool = (schoolId) =>
    !!(schoolId && teacher?.getMeta().isLicensed() && teacher?.getLicenseStatusForSchool(schoolId));

  const isLicensedStaffAtSchool = getIsLicensedForSchool(selectedSchoolId);

  const onFormSubmit = async (e) => {
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const schoolId = formData.get('schoolId');
    const validationErrors = validateForm({
      name,
      schoolId,
      gradeLevels: selectedGradeLevels
    });
    if (validationErrors) {
      setFormErrors(validationErrors);
      return;
    }
    createClassroomCb({
      teacherId: masqueradeStore.getUserIdByMasqueradeState(),
      schoolId,
      name,
      gradeLevelIds: selectedGradeLevels.map((gradeLevel) => gradeLevel.getId()),
      applyStudentsToSchoolLicense
    })
      .then((createdClassroom) => {
        // createClassroomCb never rejects (errors land in its status); no model back = the save failed.
        if (!createdClassroom) {
          addToast({
            color: 'negative',
            title: 'Error',
            message: 'Something went wrong with your request. Please try again.'
          });
          return;
        }
        invalidateClassroomsQueries();
        addToast({
          color: 'positive',
          title: 'Success!',
          message: `${name} created!`
        });
        if (typeof onSubmit === 'function') {
          onSubmit(createdClassroom);
        }
      })
      .finally(handleClose);
  };

  return (
    <Modal ariaLabel='Create new classroom modal' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            hideCloseBtn
            paddingLevel={0}
            className={classNames(modalContentStyle, 'w-[500px]! max-w-none!')}
          >
            <Dialogue.Body>
              <div className='border-subtle p-x2 flex w-full items-center justify-between border-b font-bold'>
                <Text as='div'>New Class</Text>
                <IconButton
                  icon='times'
                  size='s'
                  onClick={handleClose}
                  label='Close new class modal'
                />
              </div>
              <Form
                data-testid='new-classroom-form'
                onSubmit={(e) => {
                  onFormSubmit(e);
                }}
              >
                {!isReady ? (
                  <LoadingSpinner className='m-x2' size={3} />
                ) : (
                  <div className='p-x4'>
                    <TextField
                      data-testid='new-classroom-modal__class-name-field'
                      label='Class Name'
                      name='name'
                      message={formErrors.name}
                      error={!!formErrors.name}
                      disabled={isCreateClassroomPending}
                      className='mb-x2'
                      onChange={(e) => {
                        const error = formValidators.name(e.target.value);
                        setFormErrors((currState) => ({
                          ...currState,
                          name: error
                        }));
                      }}
                    />
                    <GradeSelector
                      setSelectedGradeLevels={setSelectedGradeLevels}
                      selectedGradeLevels={selectedGradeLevels}
                      errorMessage={!!formErrors.gradeLevels && formErrors.gradeLevels}
                      allGradeLevels={allGradeLevels}
                    />
                    <SelectField
                      label='School Association'
                      name='schoolId'
                      data-testid='new-classroom-modal__school-association-dropdown'
                      message={formErrors.schoolId}
                      error={!!formErrors.schoolId}
                      placeholder='Choose...'
                      options={schoolOptions}
                      disabled={isCreateClassroomPending}
                      className='mb-x2'
                      onChange={(e) => {
                        const schoolId = e.target.value;
                        const error = formValidators.schoolId(schoolId);
                        setFormErrors((currState) => ({
                          ...currState,
                          schoolId: error
                        }));
                        setSelectedSchoolId(schoolId);
                        // Default the license selection for the newly chosen school
                        setApplyStudentsToSchoolLicense(getIsLicensedForSchool(schoolId));
                      }}
                    />
                    <Text size='m' color='tertiary'>
                      Administrators at this school will have access to the class data. Contact{' '}
                      <Anchor underlined href='mailto:hello@albert.io'>
                        hello@albert.io
                      </Anchor>{' '}
                      if this is a problem.
                    </Text>
                    {selectedSchoolId && (
                      <LicenseStatusRadioGroup
                        isLicensedStaffAtSchool={isLicensedStaffAtSchool}
                        isLicensed={applyStudentsToSchoolLicense}
                        setIsLicensed={setApplyStudentsToSchoolLicense}
                        disabled={isCreateClassroomPending}
                        className='pt-x5'
                      />
                    )}
                  </div>
                )}
                <div className='border-subtle p-x2 flex justify-end border-t'>
                  <CloseButtonWrapper>
                    <Button color='secondary' className='mr-x1'>
                      Cancel
                    </Button>
                  </CloseButtonWrapper>
                  <Button
                    data-testid='create-class-button'
                    disabled={!isReady || isCreateClassroomPending}
                    type='submit'
                  >
                    Create Class
                  </Button>
                </div>
              </Form>
            </Dialogue.Body>
          </Dialogue>
        );
      }}
    </Modal>
  );
}

NewClassroomModal.propTypes = {
  handleClose: PropTypes.func,
  onSubmit: PropTypes.func
};
